import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AutoLabelDefinition, Contact, Email, EmailActionContext } from '@/types/email';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { NewSendersBanner, type PendingSenderCardData } from '@/components/NewSendersBanner';
import { PendingAliasSuggestions } from '@/components/SenderAliases';
import { fetchEmail } from '@/api/emails';
import { bulkApproveContacts, bulkBlockContacts } from '@/api/contacts';
import { fetchLabelCounts, fetchLabelDigest } from '@/api/senders';
import { fetchAutoLabels, appendAutoLabelTarget, removeMessageAutoLabel, type AutoLabelTargetInput } from '@/api/autoLabels';
import { useAutoLabelStore } from '@/stores/autoLabelStore';
import { useEmailStore } from '@/stores/emailStore';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { useSenderGrouping, type GroupingStrategy } from './hooks/useSenderGrouping';
import { clearSubjectsCache } from './hooks/useSenderSubjects';
import { useBulkSelection } from './hooks/useBulkSelection';
import { mailEvents } from '@/events/mailEvents';
import { useEmailDrag } from './hooks/useEmailDrag';
import { EmailListItem } from './components/EmailListItem';
import { BulkActionBar } from './components/BulkActionBar';
import { ListHeader } from './components/ListHeader';
import { LabelFilterBar } from './components/LabelFilterBar';
import { SenderGroupHeader } from './components/SenderGroupHeader';
import { SenderGroupItem } from './components/SenderGroupItem';
import { EmailListVirtualized } from './components/EmailListVirtualized';
import {
  BriefingItemRow,
  BriefingScopeHeader,
  BriefingSectionRow,
} from './components/BriefingRows';
import { BriefingPageHeader } from './components/BriefingPageHeader';
import { useBriefingRows, type BriefingListRow } from './hooks/useBriefingRows';
import { useBriefingStore } from '@/stores/briefingStore';
import type { BriefingItem } from '@/api/briefing';
import { useHoverStore } from '@/stores/hoverStore';
import { useContactStore } from '@/stores/contactStore';
import { useOperationStore } from '@/stores/operationStore';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useUIStore } from '@/stores/uiStore';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { AddSenderToLabelDialog } from '@/components/AddSenderToLabelDialog';
import { EmailTaskCreationProvider, useEmailTaskCreation } from '../email-detail/components/EmailTaskCreation';
import { EmailEventCreationProvider, useEmailEventCreation } from '../email-detail/components/EmailEventCreation';
import { useMessageSummary } from '@/hooks/useMessageSummary';
import type {
  ActiveSenderGroup,
  SenderGroupInfo,
  SenderGroupQuickAction,
} from './types';
import type { SenderGroupEntry } from './hooks/useSenderGrouping';

const NO_LABEL_FILTER_ID = '__no_label__';
const MAX_PENDING_AUTO_LOAD_ATTEMPTS = 3;
const MAX_LABEL_FILTER_AUTO_LOAD_ATTEMPTS = 20; // Allow more attempts since labels may be sparse
const MIN_PENDING_VISIBLE_EMAILS = 12;
const MIN_LABEL_FILTER_VISIBLE_EMAILS = 12;

const normalizePendingSenderKey = (email: Email): string => {
  if (email.contactId) {
    return `contact:${email.contactId}`;
  }
  const normalizedEmail = email.from?.email?.trim().toLowerCase();
  if (normalizedEmail) {
    return `email:${normalizedEmail}`;
  }
  return email.id;
};

const normalizePendingContactKey = (contact: Contact): string => {
  if (contact.id) {
    return `contact:${contact.id}`;
  }
  const normalizedEmail = contact.email?.trim().toLowerCase();
  if (normalizedEmail) {
    return `email:${normalizedEmail}`;
  }
  return `contact:unknown:${contact.email ?? 'pending'}`;
};

const getEmailTimestamp = (email: Email): number => {
  const dateValue = email.date;
  if (dateValue instanceof Date) {
    return dateValue.getTime();
  }
  const timestamp = new Date(dateValue).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const toDateOrNull = (value: Date | string | number | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const mergeAutoLabels = (
  current: Email['autoLabels'] = [],
  next: Email['autoLabels'][number],
): Email['autoLabels'] => {
  const existingIndex = current.findIndex((label) => label.id === next.id);
  if (existingIndex >= 0) {
    const updated = [...current];
    updated[existingIndex] = { ...updated[existingIndex], ...next };
    return updated;
  }
  return [...current, next];
};

type TaskCreationRequest = {
  email: Email;
  anchor: {
    element: HTMLElement;
    rect: DOMRect;
  };
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  width?: number;
  key: number;
};

const findNonAccountRecipient = (email: Email, accountEmails: Set<string>): string | null => {
  if (!email.to || email.to.length === 0) {
    return null;
  }
  
  // Find the first recipient that's not one of the user's accounts
  for (const recipient of email.to) {
    const recipientEmail = recipient.email?.trim();
    if (recipientEmail) {
      const normalizedEmail = recipientEmail.toLowerCase();
      if (!accountEmails.has(normalizedEmail)) {
        return recipientEmail;
      }
    }
  }
  
  return null;
};

interface PendingSenderEntry {
  key: string;
  contactId: string | null;
  accountId: string | null;
  folderId: string | null;
  emailId: string | null;
  threadId: string | null;
  senderName: string | null;
  senderEmail: string;
  subject: string;
  snippet: string;
  date: Date | null;
  email?: Email;
  hasEmail: boolean;
  operationId: string;
  reviewRecommendation?: Contact['aiReviewRecommendation'] | null;
}

const getEntryReviewPriority = (entry: PendingSenderEntry): number => {
  const decision = entry.reviewRecommendation?.decision;
  if (decision === 'yes') {
    return 2;
  }
  if (decision === 'no') {
    return 1;
  }
  return 0;
};

interface EmailListProps {
  emails: Email[];
  selectedEmailId: string | null;
  onEmailSelect: (
    emailId: string,
    folderId?: string | null,
    accountId?: string | null,
    threadId?: string | null,
  ) => void;
  onLoadMore?: () => void;
  onForceLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  labelFilter: string | 'all';
  onLabelFilterChange: (labelId: string) => void;
  bulkSelection: Set<string>;
  onToggleBulkSelect: (emailId: string) => void;
  onSelectAllVisible: (emailIds: string[]) => void;
  onClearSelection: () => void;
  isBulkEditMode: boolean;
  onBulkEditModeChange: (enabled: boolean) => void;
  onEmailMarkRead: (emailId: string) => void;
  onEmailMarkUnread: (emailId: string) => void;
  onEmailArchive: (emailId: string, context?: EmailActionContext) => void;
  onEmailDelete: (emailId: string) => void;
  onEmailBlockSender: (emailId: string) => void;
  onEmailApproveSender: (emailId: string) => void;
  onEmailReply?: (emailId: string) => void;
  onEmailReplyAll?: (emailId: string) => void;
  onEmailForward?: (emailId: string) => void;
  onCreateTask?: (emailId: string, anchor?: HTMLElement | null) => void;
  onAddSenderLabel?: (emailId: string) => void;
  onTrainSpam?: (emailId: string) => void;
  onTrainGood?: (emailId: string) => void;
  onViewPendingContacts: () => void;
  isLoadingInitial?: boolean;
  operationStates?: Record<string, 'pending' | 'error'>;
  onRefresh?: () => void;
  accountColors?: Record<string, string | null | undefined>;
  showAccountColors?: boolean;
  onEmailDragStart?: (email: Email) => void;
  onEmailDragEnd?: () => void;
  draggingEmailId?: string | null;
  draggingSenderGroupKey?: string | null;
  draggingSenderGroupCount?: number | null;
  onSenderGroupDragStart?: (group: SenderGroupInfo) => void;
  onSenderGroupDragEnd?: () => void;
  groupBySender?: boolean;
  groupingStrategy?: GroupingStrategy;
  activeSenderGroup?: ActiveSenderGroup | null;
  onSenderGroupSelect?: (group: SenderGroupInfo) => void;
  onSenderGroupOpenLatestEmail?: (group: SenderGroupInfo, email: Email) => void;
  onExitSenderGroup?: (options?: { skipReload?: boolean }) => void;
  onShowDigest?: (group: SenderGroupInfo) => void;
  onSenderArchiveAll?: (group: SenderGroupInfo) => Promise<void> | void;
  onSenderDeleteAll?: (group: SenderGroupInfo) => Promise<void> | void;
  onSenderGroupAction?: (group: SenderGroupInfo, action: SenderGroupQuickAction) => Promise<void> | void;
  pendingGroupActions?: Record<string, 'archive' | 'delete' | 'block' | 'approve' | null>;
  isSenderDigestLoading?: boolean;
  isSenderDigestVisible?: boolean;
  selectedAccountId: string | null;
  selectedFolderId: string | null;
  showPendingSenders?: boolean;
  showRecipientNames?: boolean;
  isListVisible?: boolean;
}

export function EmailList({
  emails,
  selectedEmailId,
  onEmailSelect,
  onLoadMore,
  onForceLoadMore,
  hasMore = false,
  isLoadingMore = false,
  labelFilter,
  onLabelFilterChange,
  bulkSelection,
  onToggleBulkSelect,
  onSelectAllVisible,
  isBulkEditMode,
  onBulkEditModeChange,
  onEmailMarkRead,
  onEmailMarkUnread,
  onEmailArchive,
  onEmailDelete,
  onEmailBlockSender,
  onEmailApproveSender,
  onEmailReply,
  onEmailReplyAll,
  onEmailForward,
  onCreateTask,
  onAddSenderLabel,
  onTrainSpam,
  onTrainGood,
  onViewPendingContacts,
  isLoadingInitial = false,
  operationStates = {},
  onRefresh,
  accountColors,
  showAccountColors = false,
  onEmailDragStart,
  onEmailDragEnd,
  draggingEmailId = null,
  draggingSenderGroupKey = null,
  draggingSenderGroupCount = null,
  onSenderGroupDragStart,
  onSenderGroupDragEnd,
  groupBySender = false,
  groupingStrategy = 'sender',
  activeSenderGroup = null,
  onSenderGroupSelect,
  onSenderGroupOpenLatestEmail,
  onExitSenderGroup,
  onShowDigest,
  onSenderArchiveAll,
  onSenderDeleteAll,
  onSenderGroupAction,
  pendingGroupActions = {},
  isSenderDigestLoading = false,
  isSenderDigestVisible = false,
  selectedAccountId,
  selectedFolderId,
  showPendingSenders = false,
  showRecipientNames = false,
  isListVisible = true,
}: EmailListProps) {
  const setHoveredEmail = useHoverStore((state) => state.setHoveredEmail);
  const pendingContacts = useContactStore((state) => state.pendingContacts);
  const contacts = useContactStore((state) => state.contacts);
  const pendingContactsLoading = useContactStore((state) => state.pendingContactsLoading);
  const reviewRecommendations = useContactStore((state) => state.reviewRecommendations);
  const approveContactDirect = useContactStore((state) => state.approveContact);
  const blockContactDirect = useContactStore((state) => state.blockContact);
  const markOperationPending = useOperationStore((state) => state.markOperationPending);
  const markOperationComplete = useOperationStore((state) => state.markOperationComplete);
  const markOperationError = useOperationStore((state) => state.markOperationError);
  const accounts = useAccountStore((state) => state.accounts);

  // Compute account emails early so it can be used in pendingData filtering
  const accountEmails = useMemo(() => {
    const emailSet = new Set<string>();
    accounts.forEach((account) => {
      if (account.emailAddress) {
        emailSet.add(account.emailAddress.trim().toLowerCase());
      }
    });
    return emailSet;
  }, [accounts]);

  const labelsVisible = useUIStore((state) => state.labelsVisible);
  const mailView = useUIStore((state) => state.mailView);
  const autoLabelMap = useAutoLabelStore((state) => state.labels);
  const autoLabelOrder = useAutoLabelStore((state) => state.labelOrder);
  const autoLabelsInitialized = useAutoLabelStore((state) => state.initialized);
  const setAutoLabels = useAutoLabelStore((state) => state.setLabels);
  const upsertLabel = useAutoLabelStore((state) => state.upsertLabel);
  const updateEmailInPlace = useEmailStore((state) => state.updateEmailInPlace);
  const senderLabels = useMemo(
    () =>
      autoLabelOrder
        .map((id) => autoLabelMap[id])
        .filter((label): label is AutoLabelDefinition => Boolean(label) && label.type === 'sender'),
    [autoLabelMap, autoLabelOrder]
  );

  const activeGroupKey = activeSenderGroup?.key ?? null;
  const lastPrefetchedDigestRef = useRef<string | null>(null);
  const autoLoadedForGroupRef = useRef<string | null>(null);

  const pendingFeatureEnabled = showPendingSenders && Boolean(selectedAccountId && selectedFolderId);
  // A briefing item drill-down shows an EXPLICIT set of referenced emails —
  // siphoning pending-sender refs into the (hidden) banner there silently
  // dropped rows (e.g. a "2 messages" item listing 1 because its sender was
  // still pending review). Read directly from the store: the scope is set
  // before the drill-down list renders, and this flag feeds memos above the
  // main briefing hook.
  const briefingScopeState = useBriefingStore((state) => state.scope);
  const shouldIsolatePendingSenders = pendingFeatureEnabled && briefingScopeState?.type !== 'item';

  const contactNameLookup = useMemo(() => {
    const map = new Map<string, string>();
    const ingest = (entries: { email?: string | null; displayName?: string | null }[] | undefined) => {
      if (!entries?.length) {
        return;
      }
      entries.forEach((contact) => {
        const email = contact.email?.trim().toLowerCase();
        const displayName = contact.displayName?.trim();
        if (!email || !displayName) {
          return;
        }
        if (!map.has(email)) {
          map.set(email, displayName);
        }
      });
    };
    ingest(contacts);
    ingest(pendingContacts);
    return map;
  }, [contacts, pendingContacts]);

  const [pendingBulkAction] = useState<'approve' | 'block' | null>(null);
  const [pendingBulkProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);

  useEffect(() => {
    if (autoLabelsInitialized) {
      return;
    }
    let cancelled = false;
    fetchAutoLabels()
      .then((fetched) => {
        if (!cancelled) {
          setAutoLabels(fetched);
        }
      })
      .catch((error) => {
        console.error('Failed to load auto labels', error);
      });
    return () => {
      cancelled = true;
    };
  }, [autoLabelsInitialized, setAutoLabels]);

  const storedAutoLabels = useMemo<AutoLabelDefinition[]>(() => {
    return autoLabelOrder
      .map((id) => autoLabelMap[id])
      .filter((label): label is AutoLabelDefinition => Boolean(label));
  }, [autoLabelMap, autoLabelOrder]);

  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelDialogEmailId, setLabelDialogEmailId] = useState<string | null>(null);
  const [selectedLabelId, setSelectedLabelId] = useState<string>('');
  const [labelDialogError, setLabelDialogError] = useState<string | null>(null);
  const [labelDialogPending, setLabelDialogPending] = useState(false);
  const labelDialogEmail = useMemo(
    () => emails.find((email) => email.id === labelDialogEmailId) ?? null,
    [emails, labelDialogEmailId]
  );

  useEffect(() => {
    if (selectedLabelId || !senderLabels.length) {
      return;
    }
    setSelectedLabelId(senderLabels[0].id);
  }, [selectedLabelId, senderLabels]);

  const [taskCreationRequest, setTaskCreationRequest] = useState<TaskCreationRequest | null>(null);
  const [eventCreationEmail, setEventCreationEmail] = useState<Email | null>(null);

  // Single pass: build all pending data structures at once
  const pendingData = useMemo(() => {
    if (!pendingFeatureEnabled) {
      return {
        pendingEmailIds: new Set<string>(),
        pendingEmailBySender: new Map<string, Email>(),
        pendingSenderEntries: [],
        emailsForList: emails,
      };
    }

    if (!shouldIsolatePendingSenders) {
      return {
        pendingEmailIds: new Set<string>(),
        pendingEmailBySender: new Map<string, Email>(),
        pendingSenderEntries: [],
        emailsForList: emails,
      };
    }

    // Single pass: build all data structures at once
    const pendingIds = new Set<string>();
    const bySender = new Map<string, Email>();
    const nonPendingEmails: Email[] = [];

    const resolveReviewRecommendation = (
      contact: Contact | undefined,
      fallbackContactId?: string | null
    ): Contact['aiReviewRecommendation'] | null => {
      if (contact?.aiReviewRecommendation) {
        return contact.aiReviewRecommendation;
      }
      const contactId = contact?.id ?? fallbackContactId ?? null;
      if (!contactId) {
        return null;
      }
      return reviewRecommendations[contactId] ?? null;
    };

    // Single iteration over emails
    emails.forEach((email) => {
      // Skip emails sent by the user - they shouldn't appear in pending senders
      const senderEmail = email.from?.email?.trim().toLowerCase();
      const isSentByUser = senderEmail && accountEmails.has(senderEmail);

      if (email.contactApprovalState === 'pending' && !isSentByUser) {
        pendingIds.add(email.id);
        const senderKey = normalizePendingSenderKey(email);
        const existing = bySender.get(senderKey);
        if (!existing || getEmailTimestamp(email) > getEmailTimestamp(existing)) {
          bySender.set(senderKey, email);
        }
      } else {
        nonPendingEmails.push(email);
      }
    });

    // Build entries (only iterates contacts and the pending map, not all emails)
    const entries: PendingSenderEntry[] = [];
    const contactByKey = new Map<string, Contact>();
    const seenKeys = new Set<string>();

    const addEntryFromEmail = (email: Email, key: string) => {
      const contact = contactByKey.get(key);
      entries.push({
        key,
        contactId: email.contactId ?? null,
        accountId: email.accountId ?? selectedAccountId ?? null,
        folderId: email.folderId ?? selectedFolderId ?? null,
        emailId: email.id,
        threadId: email.threadId ?? null,
        senderName: email.from?.name ?? null,
        senderEmail: email.from?.email ?? 'Unknown sender',
        subject: email.subject?.trim() || 'No subject',
        snippet: email.snippet?.trim() || 'No preview available',
        date: toDateOrNull(email.date),
        email,
        hasEmail: true,
        operationId: email.id,
        reviewRecommendation: resolveReviewRecommendation(contact, email.contactId ?? null),
      });
      seenKeys.add(key);
    };

    const addEntryFromContact = (contact: Contact, email: Email | undefined, key: string) => {
      const messageId = email?.id ?? contact.lastMessagePreview?.messageId ?? null;
      if (!messageId) {
        return;
      }
      const date =
        toDateOrNull(email?.date) ||
        toDateOrNull(contact.lastMessageAt) ||
        toDateOrNull(contact.lastActivityAt) ||
        toDateOrNull(contact.updatedAt) ||
        toDateOrNull(contact.createdAt);
      const subject =
        email?.subject?.trim() ||
        contact.lastMessagePreview?.subject?.trim() ||
        'No subject';
      const snippet =
        email?.snippet?.trim() ||
        contact.lastMessagePreview?.snippet?.trim() ||
        contact.lastMessagePreview?.text?.trim() ||
        contact.lastMessagePreview?.html?.trim() ||
        'No preview available';
      entries.push({
        key,
        contactId: contact.id ?? null,
        accountId: email?.accountId ?? contact.accountId ?? selectedAccountId ?? null,
        folderId: email?.folderId ?? selectedFolderId ?? null,
        emailId: messageId,
        threadId: email?.threadId ?? null,
        senderName: contact.displayName ?? email?.from?.name ?? null,
        senderEmail: contact.email || email?.from?.email || 'Unknown sender',
        subject,
        snippet,
        date,
        email,
        hasEmail: Boolean(email),
        operationId: email?.id ?? `contact:${contact.id ?? messageId}`,
        reviewRecommendation: resolveReviewRecommendation(contact, contact.id ?? email?.contactId ?? null),
      });
      seenKeys.add(key);
    };

    pendingContacts.forEach((contact) => {
      const key = normalizePendingContactKey(contact);
      contactByKey.set(key, contact);
      const email = bySender.get(key);
      addEntryFromContact(contact, email, key);
    });

    bySender.forEach((email, key) => {
      if (!seenKeys.has(key)) {
        addEntryFromEmail(email, key);
      }
    });

    entries.sort((a, b) => {
      const reviewDiff = getEntryReviewPriority(b) - getEntryReviewPriority(a);
      if (reviewDiff !== 0) {
        return reviewDiff;
      }
      const aTime = a.date?.getTime() ?? 0;
      const bTime = b.date?.getTime() ?? 0;
      return bTime - aTime;
    });

    return {
      pendingEmailIds: pendingIds,
      pendingEmailBySender: bySender,
      pendingSenderEntries: entries,
      emailsForList: nonPendingEmails,
    };
  }, [
    emails,
    pendingContacts,
    pendingFeatureEnabled,
    shouldIsolatePendingSenders,
    selectedAccountId,
    selectedFolderId,
    reviewRecommendations,
    accountEmails,
  ]);

  // Destructure for use in component
  const { pendingSenderEntries, emailsForList } = pendingData;


  // Label counts per folder (from server)
  const [folderLabelCounts, setFolderLabelCounts] = useState<Map<string, number>>(new Map());
  const [folderNoLabelCount, setFolderNoLabelCount] = useState(0);
  const labelCountsAbortRef = useRef<AbortController | null>(null);
  const lastLabelCountsKeyRef = useRef<string | null>(null);

  // Server-side label filtering state
  // (A1: label-filtered emails live in the EMAIL STORE as a listSource —
  // actions and realtime affect them like any other list. Only pagination
  // bookkeeping stays local.)
  const [labelFilterTotalMessages, setLabelFilterTotalMessages] = useState<number>(0);
  const [isLoadingLabelFilter, setIsLoadingLabelFilter] = useState(false);
  // Forces the label-filter fetch effect to re-run when a racing mailbox load
  // overwrote the label list (N13).
  const [labelFilterRetryNonce, setLabelFilterRetryNonce] = useState(0);
  const labelFilterFailedKeyRef = useRef<string | null>(null);
  const [labelFilterOffset, setLabelFilterOffset] = useState(0);
  const labelFilterAbortRef = useRef<AbortController | null>(null);
  const lastLabelFilterKeyRef = useRef<string | null>(null);
  const LABEL_FILTER_PAGE_SIZE = 50;
  const {
    labelFilterOptions,
    hasLabelAssignments,
    availableLabelIds,
    hasUnlabeledEmails,
    noLabelFilterOption,
  } = useMemo(() => {
    const optionMap = new Map<string, { id: string; name: string; color?: string | null }>();
    let hasUnlabeled = false;
    let hasLabels = storedAutoLabels.length > 0;
    const available = new Set<string>();

    storedAutoLabels.forEach((label) => {
      optionMap.set(label.id, {
        id: label.id,
        name: label.name,
        color: label.color ?? null,
      });
    });

    emailsForList.forEach((email) => {
      if (!email.autoLabels.length) {
        hasUnlabeled = true;
      }
      email.autoLabels.forEach((label) => {
        hasLabels = true;
        available.add(label.id);
        const existing = optionMap.get(label.id);
        const color = label.color ?? existing?.color ?? null;
        if (!existing) {
          optionMap.set(label.id, { id: label.id, name: label.name, color });
        } else if (!existing.color && color) {
          optionMap.set(label.id, { ...existing, color });
        }
      });
    });

    // Filter options to only include labels that have emails in this folder
    // Use folderLabelCounts from the server if available, otherwise fall back to availableLabelIds
    const hasFolderCounts = folderLabelCounts.size > 0 || folderNoLabelCount > 0;
    const options = Array.from(optionMap.values())
      .filter((label) => {
        // If we have folder counts, filter by count > 0
        if (hasFolderCounts) {
          return (folderLabelCounts.get(label.id) ?? 0) > 0;
        }
        // Fall back to checking if any email in current list has the label
        return available.has(label.id);
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    // Show "no labels" option if there are unlabeled emails in this folder
    const showNoLabelOption = hasFolderCounts
      ? folderNoLabelCount > 0
      : hasLabels && hasUnlabeled;
    const noLabelOption = showNoLabelOption
      ? { id: NO_LABEL_FILTER_ID, name: 'no labels', color: null }
      : null;

    return {
      labelFilterOptions: options,
      hasLabelAssignments: hasLabels,
      availableLabelIds: available,
      hasUnlabeledEmails: hasUnlabeled,
      noLabelFilterOption: noLabelOption,
    };
  }, [emailsForList, storedAutoLabels, folderLabelCounts, folderNoLabelCount]);

  // Feature flag: hide all label filter UI while the flag is off. Labels are
  // still applied server-side and the persisted per-folder filter is kept.
  const showLabelFilterUi = labelsVisible && hasLabelAssignments;

  // (A14: the per-folder label filter is PERSISTED — no reset on folder entry.
  // The validation effect below clears filters that don't exist in the folder.)

  // Fetch label counts for the current folder
  useEffect(() => {
    if (!selectedAccountId || !selectedFolderId || !labelsVisible) {
      setFolderLabelCounts(new Map());
      setFolderNoLabelCount(0);
      lastLabelCountsKeyRef.current = null;
      return;
    }

    const countsKey = `${selectedAccountId}:${selectedFolderId}`;
    if (lastLabelCountsKeyRef.current === countsKey) {
      return;
    }

    // Abort previous request
    if (labelCountsAbortRef.current) {
      labelCountsAbortRef.current.abort();
    }

    const abortController = new AbortController();
    labelCountsAbortRef.current = abortController;
    lastLabelCountsKeyRef.current = countsKey;

    fetchLabelCounts({
      accountId: selectedAccountId,
      folderId: selectedFolderId,
      signal: abortController.signal,
    })
      .then((result) => {
        if (abortController.signal.aborted) {
          return;
        }
        const countsMap = new Map<string, number>();
        result.labelCounts.forEach(({ labelId, count }) => {
          countsMap.set(labelId, count);
        });
        setFolderLabelCounts(countsMap);
        setFolderNoLabelCount(result.noLabelCount);
      })
      .catch((error) => {
        if (abortController.signal.aborted || error?.name === 'AbortError') {
          return;
        }
        console.error('[LabelCounts] Failed to fetch label counts', error);
      });

    return () => {
      abortController.abort();
    };
  }, [selectedAccountId, selectedFolderId, labelsVisible]);

  // Build a set of all valid label IDs (from stored labels + emails) for filter validation
  const validLabelIds = useMemo(() => {
    const ids = new Set(availableLabelIds);
    storedAutoLabels.forEach((label) => ids.add(label.id));
    return ids;
  }, [availableLabelIds, storedAutoLabels]);

  useEffect(() => {
    if (labelFilter === 'all') {
      return;
    }
    const hasFolderCounts = folderLabelCounts.size > 0 || folderNoLabelCount > 0;

    if (labelFilter === NO_LABEL_FILTER_ID) {
      // Reset if there are no unlabeled emails in this folder
      if (hasFolderCounts && folderNoLabelCount === 0) {
        onLabelFilterChange('all');
        return;
      }
      // Fallback: Only reset if hasLabelAssignments is true but hasUnlabeledEmails is false
      if (!hasFolderCounts && hasLabelAssignments && !hasUnlabeledEmails) {
        onLabelFilterChange('all');
      }
      return;
    }

    // Check if the selected label has emails in this folder
    if (hasFolderCounts) {
      const count = folderLabelCounts.get(labelFilter) ?? 0;
      if (count === 0) {
        onLabelFilterChange('all');
        return;
      }
    }

    // Fallback: Use validLabelIds (stored + available) for validation
    if (!validLabelIds.has(labelFilter)) {
      onLabelFilterChange('all');
    }
  }, [validLabelIds, hasLabelAssignments, hasUnlabeledEmails, labelFilter, folderLabelCounts, folderNoLabelCount]);

  // Server-side label filtering effect
  useEffect(() => {
    // Debug logging
    if (import.meta.env.DEV) {
      console.log('[LabelFilter] Effect triggered:', {
        labelFilter,
        selectedAccountId,
        selectedFolderId,
        lastKey: lastLabelFilterKeyRef.current,
      });
    }

    // Reset state when filter changes to 'all'
    if (labelFilter === 'all') {
      if (labelFilterAbortRef.current) {
        labelFilterAbortRef.current.abort();
        labelFilterAbortRef.current = null;
      }
      setLabelFilterTotalMessages(0);
      setLabelFilterOffset(0);
      lastLabelFilterKeyRef.current = null;
      // If the store list is still the label-filtered set, reload the mailbox
      if (useEmailStore.getState().listSource.kind === 'labelFilter') {
        onRefresh?.();
      }
      return;
    }

    // Need account and folder context to fetch
    if (!selectedAccountId || !selectedFolderId) {
      if (import.meta.env.DEV) {
        console.log('[LabelFilter] Missing account/folder context, skipping server fetch');
      }
      return;
    }

    // Build a key to detect when we need a fresh fetch
    const filterKey = `${selectedAccountId}:${selectedFolderId}:${labelFilter}`;
    if (lastLabelFilterKeyRef.current === filterKey) {
      if (import.meta.env.DEV) {
        console.log('[LabelFilter] Same filter key, skipping fetch');
      }
      return;
    }

    // Abort any in-flight request
    if (labelFilterAbortRef.current) {
      labelFilterAbortRef.current.abort();
    }

    const abortController = new AbortController();
    labelFilterAbortRef.current = abortController;
    lastLabelFilterKeyRef.current = filterKey;

    if (import.meta.env.DEV) {
      console.log('[LabelFilter] Fetching from server:', { filterKey, limit: LABEL_FILTER_PAGE_SIZE });
    }

    setIsLoadingLabelFilter(true);
    setLabelFilterOffset(0);

    fetchLabelDigest({
      accountId: selectedAccountId,
      folderId: selectedFolderId,
      labelId: labelFilter,
      limit: LABEL_FILTER_PAGE_SIZE,
      offset: 0,
      signal: abortController.signal,
    })
      .then((result) => {
        if (abortController.signal.aborted) {
          return;
        }
        if (import.meta.env.DEV) {
          console.log('[LabelFilter] Server response:', {
            emailCount: result.emails.length,
            totalMessages: result.totalMessages,
          });
        }
        // Single-writer path: the store owns the list (suppression-filtered
        // projection; actions/realtime prune and update these rows) (A1).
        useEmailStore.getState().replaceList(result.emails, {
          source: { kind: 'labelFilter', labelKey: filterKey },
          hasMore: result.emails.length < result.totalMessages,
        });
        labelFilterFailedKeyRef.current = null;
        setLabelFilterTotalMessages(result.totalMessages);
        setLabelFilterOffset(result.emails.length);
        setIsLoadingLabelFilter(false);
      })
      .catch((error) => {
        if (abortController.signal.aborted || error?.name === 'AbortError') {
          return;
        }
        console.error('[LabelFilter] Failed to fetch label-filtered emails', error);
        // Remember the failure so the listSource watcher below doesn't spin
        // retrying a persistently-failing fetch (N13 guard).
        labelFilterFailedKeyRef.current = filterKey;
        setIsLoadingLabelFilter(false);
      });

    return () => {
      abortController.abort();
    };
  }, [labelFilter, selectedAccountId, selectedFolderId, LABEL_FILTER_PAGE_SIZE, labelFilterRetryNonce]);

  // Load more label-filtered emails
  const loadMoreLabelFilteredEmails = useCallback(() => {
    if (!selectedAccountId || !selectedFolderId || labelFilter === 'all') {
      return;
    }
    if (isLoadingLabelFilter) {
      return;
    }
    // Use offset (not email count) to check if there are more pages
    if (labelFilterOffset >= labelFilterTotalMessages) {
      return;
    }

    const abortController = new AbortController();
    labelFilterAbortRef.current = abortController;
    setIsLoadingLabelFilter(true);

    fetchLabelDigest({
      accountId: selectedAccountId,
      folderId: selectedFolderId,
      labelId: labelFilter,
      limit: LABEL_FILTER_PAGE_SIZE,
      offset: labelFilterOffset,
      signal: abortController.signal,
    })
      .then((result) => {
        if (abortController.signal.aborted) {
          return;
        }
        // If we got an empty page or fewer emails than expected, we've reached the end
        // even if labelFilterTotalMessages suggests there should be more
        const gotEmptyPage = result.emails.length === 0;

        // Append into the store index — the projection dedupes by identity
        if (result.emails.length) {
          useEmailStore.getState().applyEmailUpserts(result.emails);
        }
        // Increment offset by page size (not emails returned) to ensure we don't re-request same rows
        // Also set to totalMessages if we got an empty page to stop pagination
        setLabelFilterOffset((prev) => gotEmptyPage ? labelFilterTotalMessages : prev + LABEL_FILTER_PAGE_SIZE);
        setIsLoadingLabelFilter(false);
      })
      .catch((error) => {
        if (abortController.signal.aborted || error?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load more label-filtered emails', error);
        setIsLoadingLabelFilter(false);
      });
  }, [
    selectedAccountId,
    selectedFolderId,
    labelFilter,
    isLoadingLabelFilter,
    labelFilterTotalMessages,
    labelFilterOffset,
    LABEL_FILTER_PAGE_SIZE,
  ]);

  // Determine if we're using server-side label filtering
  const useServerLabelFilter = labelFilter !== 'all' && selectedAccountId && selectedFolderId;
  // Use offset (not email count) to determine if there are more pages, since some emails may fail to hydrate
  const hasMoreLabelFiltered = labelFilterOffset < labelFilterTotalMessages;

  const listSourceKind = useEmailStore((state) => state.listSource.kind);

  // A mailbox load racing the label fetch can win and leave the store list as
  // `mailbox` while the filter UI stays active — and the filter-key ref then
  // suppressed any refetch, silently degrading to client-side filtering
  // (N13). Detect the mismatch and re-run the fetch (skipping keys whose
  // fetch FAILED — those must not retry-spin).
  useEffect(() => {
    if (labelFilter === 'all' || !useServerLabelFilter || isLoadingLabelFilter) {
      return;
    }
    if (listSourceKind === 'labelFilter') {
      return;
    }
    const filterKey = `${selectedAccountId}:${selectedFolderId}:${labelFilter}`;
    if (lastLabelFilterKeyRef.current !== filterKey) {
      return; // the fetch effect will run on its own
    }
    if (labelFilterFailedKeyRef.current === filterKey) {
      return;
    }
    lastLabelFilterKeyRef.current = null;
    setLabelFilterRetryNonce((nonce) => nonce + 1);
  }, [listSourceKind, labelFilter, useServerLabelFilter, isLoadingLabelFilter, selectedAccountId, selectedFolderId]);

  const filteredEmails = useMemo(() => {
    if (labelFilter === 'all') {
      return emailsForList;
    }
    // When the store list IS the server-fetched label set, the projection
    // (already suppression-filtered, action/realtime-aware) is the list (A1).
    if (useServerLabelFilter && listSourceKind === 'labelFilter') {
      return emailsForList;
    }
    // Fallback to client-side filtering (during initial load or if server fetch failed)
    if (labelFilter === NO_LABEL_FILTER_ID) {
      return emailsForList.filter((email) => email.autoLabels.length === 0);
    }
    return emailsForList.filter((email) => email.autoLabels.some((label) => label.id === labelFilter));
  }, [emailsForList, labelFilter, useServerLabelFilter, listSourceKind]);

  // The pending-senders banner never belongs on the Briefing tab — it's inbox
  // chrome, and the briefing page is self-contained now.
  const showPendingBanner =
    shouldIsolatePendingSenders &&
    mailView !== 'briefing' &&
    pendingSenderEntries.length > 0 &&
    !activeGroupKey &&
    !briefingScopeState;

  const pendingSenderMap = useMemo(() => {
    if (!pendingFeatureEnabled) {
      return new Map<string, PendingSenderEntry>();
    }
    const map = new Map<string, PendingSenderEntry>();
    pendingSenderEntries.forEach((entry) => {
      map.set(entry.key, entry);
    });
    return map;
  }, [pendingSenderEntries, pendingFeatureEnabled]);

  // Cache for fetched email data (for entries where email wasn't in the paginated list)
  const [fetchedPendingEmails, setFetchedPendingEmails] = useState<Map<string, Email>>(new Map());
  const fetchingPendingEmailsRef = useRef<Set<string>>(new Set());

  // Track entries dismissed via unsubscribe (removed from display but contact stays pending)
  const [dismissedPendingKeys, setDismissedPendingKeys] = useState<Set<string>>(new Set());

  // Fetch missing email data for pending entries that don't have it
  useEffect(() => {
    if (!pendingFeatureEnabled) return;

    // Find entries that need email data fetched
    const entriesToFetch = pendingSenderEntries.filter((entry) => {
      // Already have email data from list or from cache
      if (entry.email || fetchedPendingEmails.has(entry.key)) return false;
      // Need accountId and emailId to fetch
      if (!entry.accountId || !entry.emailId) return false;
      // Already fetching this one
      if (fetchingPendingEmailsRef.current.has(entry.key)) return false;
      return true;
    });

    if (entriesToFetch.length === 0) return;

    // Fetch missing emails (limit concurrent fetches)
    const toFetch = entriesToFetch.slice(0, 5);
    toFetch.forEach((entry) => {
      fetchingPendingEmailsRef.current.add(entry.key);
      fetchEmail(entry.accountId!, entry.emailId!, entry.folderId ?? undefined)
        .then((email) => {
          setFetchedPendingEmails((prev) => new Map(prev).set(entry.key, email));
        })
        .catch((err) => {
          console.error('[EmailList] Failed to fetch pending email:', err);
        })
        .finally(() => {
          fetchingPendingEmailsRef.current.delete(entry.key);
        });
    });
  }, [pendingFeatureEnabled, pendingSenderEntries, fetchedPendingEmails]);

  const pendingSenderCards = useMemo<PendingSenderCardData[]>(() => {
    if (!pendingFeatureEnabled) {
      return [];
    }
    return pendingSenderEntries
      .filter((entry) => !dismissedPendingKeys.has(entry.key))
      .map((entry) => {
        // Use fetched email if original email data wasn't available
        const emailData = entry.email ?? fetchedPendingEmails.get(entry.key);
        const recipientEmail = emailData ? findNonAccountRecipient(emailData, accountEmails) : null;
        return {
          key: entry.key,
          accountId: entry.accountId ?? null,
          folderId: entry.folderId ?? null,
          emailId: entry.emailId ?? null,
          senderName: entry.senderName,
          senderEmail: entry.senderEmail,
          subject: entry.subject,
          snippet: entry.snippet,
          date: entry.date,
          bodyHtml: emailData?.bodyHtml ?? null,
          unsubscribeUrl: emailData?.unsubscribeUrl ?? null,
          // operationState is read directly from the store inside the card component
          // to avoid rebuilding all cards when any operation state changes
          operationState: undefined,
          operationId: entry.operationId,
          hasEmail: entry.hasEmail || fetchedPendingEmails.has(entry.key),
          recipientEmail,
          aiDecision: entry.reviewRecommendation?.decision ?? null,
          aiStatus: entry.reviewRecommendation?.status ?? null,
          aiReason: entry.reviewRecommendation?.reason ?? null,
          spamScore: emailData?.spamScore ?? null,
          spamRequiredScore: emailData?.spamRequiredScore ?? null,
          spamAction: emailData?.spamAction ?? null,
          spamReport: emailData?.spamReport ?? null,
          spamScannedAt: emailData?.spamScannedAt ?? null,
        };
      });
  }, [pendingFeatureEnabled, pendingSenderEntries, accountEmails, fetchedPendingEmails, dismissedPendingKeys]);

  // Briefing sub-view (tab): briefing items render as pinned rows above the
  // flat list (inbox folders only — showPendingSenders is the established
  // inbox gate). The remaining emails below the briefing rows are always
  // ungrouped, so sender grouping is forced off while the tab is active.
  const briefingModeActive = mailView === 'briefing' && Boolean(selectedAccountId) && showPendingSenders;

  const { visibleEmails, senderGroups: allSenderGroups, activeSenderData, activeSenderInfo, showGroupedSummary } = useSenderGrouping({
    emails: filteredEmails,
    groupBySender: briefingModeActive ? false : groupBySender,
    activeGroupKey,
    activeSenderGroup,
    groupingStrategy,
    accountEmails,
  });
  const {
    briefingRows,
    header: briefingHeader,
    scope: briefingScope,
    scopeItem: briefingScopeItem,
    scopeItemRefs: briefingScopeItemRefs,
    pendingKeys: briefingPendingKeys,
    isLoading: briefingLoading,
    refsForItem: briefingRefsForItem,
    relatedForItem: briefingRelatedForItem,
    itemByKey: briefingItemByKey,
  } = useBriefingRows({
    enabled: briefingModeActive,
    accountId: selectedAccountId,
    emails,
  });

  const renderedEmails = useMemo(() => {
    const seen = new Set<string>();
    const result: Email[] = [];
    visibleEmails.forEach((email) => {
      if (!email) {
        return;
      }
      const key = `${email.accountId ?? 'unknown'}:${email.folderId ?? 'unknown'}:${email.id}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      result.push(email);
    });
    return result;
  }, [visibleEmails]);

  // A briefing item drill-down shows ONLY the item's referenced messages.
  // The scope load seeds the store with those refs, but realtime arrivals and
  // reloads merge unrelated mail into the same store list — pin the projection
  // to the item's refs so it can't pick strays up.
  const scopeConstrainedEmails = useMemo(() => {
    if (!briefingModeActive || briefingScope?.type !== 'item' || !briefingScopeItem) {
      return renderedEmails;
    }
    const allowed = new Set(
      briefingScopeItem.messageRefs
        .map((ref) => (ref.message_id ? String(ref.message_id) : null))
        .filter((id): id is string => Boolean(id))
    );
    return renderedEmails.filter((email) => allowed.has(email.id));
  }, [briefingModeActive, briefingScope, briefingScopeItem, renderedEmails]);

  // Filter out empty sender groups to prevent blank rows in the virtualizer
  const senderGroups = useMemo(() => {
    return allSenderGroups.filter((group) => group.emails.length > 0);
  }, [allSenderGroups]);

  // Clear subjects cache when entering grouped view to ensure fresh data
  const prevShowGroupedSummaryRef = useRef(showGroupedSummary);
  useEffect(() => {
    // Clear cache when entering grouped view (switching from individual to grouped)
    if (showGroupedSummary && !prevShowGroupedSummaryRef.current) {
      clearSubjectsCache();
    }
    prevShowGroupedSummaryRef.current = showGroupedSummary;
  }, [showGroupedSummary]);

  const activeSenderGroupAction = activeGroupKey ? pendingGroupActions[activeGroupKey] ?? null : null;
  const senderActionInProgress = Boolean(activeSenderGroupAction);
  const requestExitSenderGroup = useCallback(
    (options?: { forceReload?: boolean }) => {
      if (!onExitSenderGroup) {
        return;
      }
      if (!options?.forceReload && senderActionInProgress) {
        onExitSenderGroup({ skipReload: true });
      } else {
        onExitSenderGroup();
      }
    },
    [onExitSenderGroup, senderActionInProgress]
  );

  // (Removed: the auto-exit-when-empty effect was dead code — its
  // !activeSenderGroupInfo condition can never be true inside a group. The
  // engine's removal path re-reads state AFTER removal and exits an emptied
  // group via removeEmailsFromStore.)

  // Removed automatic digest prefetch - the digest should only be fetched when
  // the user explicitly clicks to open it, not automatically on group entry
  useEffect(() => {
    if (!groupBySender || !activeGroupKey) {
      if (!activeGroupKey) {
        lastPrefetchedDigestRef.current = null;
      }
    }
  }, [groupBySender, activeGroupKey]);

  useEffect(() => {
    if (!onLoadMore || !groupBySender || !activeGroupKey) {
      // Reset the ref when exiting sender group
      if (!activeGroupKey) {
        autoLoadedForGroupRef.current = null;
      }
      return;
    }
    // Only auto-load once per group entry to prevent infinite calls
    if (autoLoadedForGroupRef.current === activeGroupKey) {
      return;
    }
    if (!hasMore || isLoadingMore) {
      return;
    }
    autoLoadedForGroupRef.current = activeGroupKey;
    onLoadMore();
  }, [activeGroupKey, groupBySender, hasMore, isLoadingMore, onLoadMore]);

  const autoRefreshAttemptedRef = useRef(false);
  const pendingAutoLoadAttemptsRef = useRef(0);

  // When pending senders hide most or all emails, automatically pull more data until we have enough visible rows.
  // If we never surface enough visible emails after a few attempts, fall back to a full refresh.
  useEffect(() => {
    const resetTracking = () => {
      pendingAutoLoadAttemptsRef.current = 0;
      autoRefreshAttemptedRef.current = false;
    };

    if (!shouldIsolatePendingSenders || !showPendingBanner || pendingSenderEntries.length === 0) {
      resetTracking();
      return;
    }
    if (visibleEmails.length >= MIN_PENDING_VISIBLE_EMAILS) {
      resetTracking();
      return;
    }
    if (isLoadingInitial || isLoadingMore) {
      return;
    }

    const loadMoreFn = hasMore ? onLoadMore : onForceLoadMore;

    if (loadMoreFn) {
      if (pendingAutoLoadAttemptsRef.current >= MAX_PENDING_AUTO_LOAD_ATTEMPTS) {
        if (onRefresh && !autoRefreshAttemptedRef.current) {
          autoRefreshAttemptedRef.current = true;
          pendingAutoLoadAttemptsRef.current = 0;
          onRefresh();
        }
        return;
      }

      pendingAutoLoadAttemptsRef.current += 1;
      loadMoreFn();
      return;
    }

    if (onRefresh && !autoRefreshAttemptedRef.current) {
      autoRefreshAttemptedRef.current = true;
      pendingAutoLoadAttemptsRef.current = 0;
      onRefresh();
    }
  }, [
    shouldIsolatePendingSenders,
    showPendingBanner,
    pendingSenderEntries.length,
    visibleEmails.length,
    isLoadingInitial,
    isLoadingMore,
    hasMore,
    onLoadMore,
    onForceLoadMore,
    onRefresh,
  ]);

  // When label filtering results in few visible emails, automatically load more
  // to fill the screen with matching emails (only used for client-side filtering fallback)
  const labelFilterAutoLoadAttemptsRef = useRef(0);
  const lastLabelFilterRef = useRef<string | null>(null);

  useEffect(() => {
    // Reset attempts when label filter changes
    if (lastLabelFilterRef.current !== labelFilter) {
      lastLabelFilterRef.current = labelFilter;
      labelFilterAutoLoadAttemptsRef.current = 0;
    }

    // Skip if using server-side label filtering
    if (useServerLabelFilter) {
      return;
    }

    // Only apply when actively filtering by a label (not 'all')
    if (labelFilter === 'all') {
      return;
    }

    // Don't interfere if we're in a sender group view
    if (groupBySender && activeGroupKey) {
      return;
    }

    // Check if we have enough visible emails
    if (filteredEmails.length >= MIN_LABEL_FILTER_VISIBLE_EMAILS) {
      return;
    }

    // Don't load if already loading
    if (isLoadingInitial || isLoadingMore) {
      return;
    }

    // Don't load if there's nothing more to load
    if (!hasMore) {
      return;
    }

    // Limit auto-load attempts to prevent infinite loops
    if (labelFilterAutoLoadAttemptsRef.current >= MAX_LABEL_FILTER_AUTO_LOAD_ATTEMPTS) {
      return;
    }

    // Trigger load more
    if (onLoadMore) {
      labelFilterAutoLoadAttemptsRef.current += 1;
      onLoadMore();
    }
  }, [
    labelFilter,
    filteredEmails.length,
    emailsForList.length,
    groupBySender,
    activeGroupKey,
    isLoadingInitial,
    isLoadingMore,
    hasMore,
    onLoadMore,
    useServerLabelFilter,
  ]);

  const { visibleEmailIds, allVisibleSelected } = useBulkSelection({
    visibleEmails,
    bulkSelection,
  });

  const selectedEmailsAll = useMemo(
    () => filteredEmails.filter((email) => bulkSelection.has(email.id)),
    [filteredEmails, bulkSelection],
  );
  const canApproveSenders = useMemo(
    () =>
      selectedEmailsAll.some(
        (email) => email.contactApprovalState === 'pending' || email.contactApprovalState === 'blocked',
      ),
    [selectedEmailsAll],
  );
  const canBlockSenders = useMemo(
    () => selectedEmailsAll.some((email) => email.contactApprovalState === 'pending'),
    [selectedEmailsAll],
  );

  const getFallbackEmailId = useCallback(
    (currentEmailId: string): string | null => {
      if (visibleEmails.length <= 1) {
        return null;
      }

      const currentIndex = visibleEmails.findIndex((email) => email.id === currentEmailId);
      if (currentIndex === -1) {
        return visibleEmails[0]?.id ?? null;
      }

      const next = visibleEmails[currentIndex + 1];
      if (next) {
        return next.id;
      }

      const previous = visibleEmails[currentIndex - 1];
      return previous ? previous.id : null;
    },
    [visibleEmails]
  );

  const runSenderModerationAction = useCallback(
    (emailId: string, action: (targetEmailId: string) => Promise<void> | void) => {
      const existsInVisibleList = visibleEmails.some((email) => email.id === emailId);
      const previousHover = useHoverStore.getState().hoveredEmailId;
      const shouldAdjustHover =
        existsInVisibleList && (previousHover === emailId || previousHover === null);
      const fallbackId = shouldAdjustHover ? getFallbackEmailId(emailId) : null;

      if (shouldAdjustHover) {
        setHoveredEmail(fallbackId ?? null);
      }

      let result: Promise<void> | void;
      try {
        result = action(emailId);
      } catch (error) {
        if (shouldAdjustHover) {
          setHoveredEmail(previousHover);
        }
        throw error;
      }

      if (result && typeof (result as Promise<unknown>).then === 'function') {
        (result as Promise<unknown>).catch(() => {
          if (shouldAdjustHover) {
            setHoveredEmail(previousHover);
          }
        });
      }

      return result;
    },
    [getFallbackEmailId, setHoveredEmail, visibleEmails]
  );

  const handleOpenLabelDialog = useCallback(
    (emailId: string) => {
      const target = emails.find((email) => email.id === emailId);
      if (!target) {
        return;
      }
      setLabelDialogEmailId(emailId);
      if (!selectedLabelId && senderLabels.length) {
        setSelectedLabelId(senderLabels[0].id);
      }
      setLabelDialogError(null);
      setLabelDialogOpen(true);
    },
    [emails, selectedLabelId, senderLabels]
  );

  const handleConfirmLabelDialog = useCallback(async () => {
    if (!labelDialogEmail || !selectedLabelId) {
      return;
    }
    const senderEmail = labelDialogEmail.from?.email;
    if (!senderEmail && !labelDialogEmail.contactId) {
      setLabelDialogError('Sender email unavailable');
      return;
    }
    const targetPayload: AutoLabelTargetInput = {
      matchType: (labelDialogEmail.contactId ? 'contact' : 'email') as 'contact' | 'email',
      contactId: labelDialogEmail.contactId ?? undefined,
      value: labelDialogEmail.contactId ? undefined : senderEmail,
    };
    setLabelDialogPending(true);
    setLabelDialogError(null);
    try {
      const updatedTargets = await appendAutoLabelTarget(selectedLabelId, targetPayload, {
        accountId: labelDialogEmail.accountId,
        messageId: labelDialogEmail.id,
        senderEmail: senderEmail ?? undefined,
        contactState: labelDialogEmail.contactApprovalState ?? null,
      });
      const labelDef = senderLabels.find((label) => label.id === selectedLabelId);
      if (labelDef) {
        upsertLabel({ ...labelDef, targets: updatedTargets });
        const nextAssignment = {
          id: labelDef.id,
          name: labelDef.name,
          type: 'sender',
          source: 'sender',
          reason: null,
          color: labelDef.color ?? null,
        } as Email['autoLabels'][number];
        updateEmailInPlace(labelDialogEmail.id, {
          autoLabels: mergeAutoLabels(labelDialogEmail.autoLabels || [], nextAssignment),
        });
      }
      setLabelDialogOpen(false);
      setLabelDialogEmailId(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update label';
      setLabelDialogError(message);
    } finally {
      setLabelDialogPending(false);
    }
  }, [labelDialogEmail, selectedLabelId, senderLabels, upsertLabel, updateEmailInPlace]);

  const handleCreateTaskAction = useCallback(
    (emailId: string, anchor?: HTMLElement | null) => {
      const target = emails.find((email) => email.id === emailId);
      if (target && anchor instanceof HTMLElement) {
        const rect = anchor.getBoundingClientRect();
        setTaskCreationRequest({
          email: target,
          anchor: { element: anchor, rect: new DOMRect(rect.x, rect.y, rect.width, rect.height) },
          align: 'center',
          side: 'bottom',
          width: 720,
          key: Date.now(),
        });
        return;
      }
      onCreateTask?.(emailId, anchor);
    },
    [emails, onCreateTask]
  );

  const taskCreationEmail = taskCreationRequest?.email ?? null;
  const taskCreationSummary = useMessageSummary(taskCreationEmail);

  const handleApproveSender = useCallback(
    (emailId: string) => runSenderModerationAction(emailId, onEmailApproveSender),
    [onEmailApproveSender, runSenderModerationAction]
  );

  const handleBlockSender = useCallback(
    (emailId: string) => runSenderModerationAction(emailId, onEmailBlockSender),
    [onEmailBlockSender, runSenderModerationAction]
  );

  // Row-bound variants: the upstream handlers now REJECT on failure (C10) so
  // awaiting callers (pending list) can revert — fire-and-forget row clicks
  // need the rejection handled here, with a visible toast.
  const handleApproveSenderFromRow = useCallback(
    (emailId: string) => {
      void Promise.resolve(handleApproveSender(emailId)).catch(() => {
        toast.error('Failed to approve sender');
      });
    },
    [handleApproveSender]
  );

  const handleBlockSenderFromRow = useCallback(
    (emailId: string) => {
      void Promise.resolve(handleBlockSender(emailId)).catch(() => {
        toast.error('Failed to block sender');
      });
    },
    [handleBlockSender]
  );

  const handleRemoveLabel = useCallback(
    async (emailId: string, labelId: string) => {
      const email = emails.find((e) => e.id === emailId);
      if (!email?.accountId) {
        return;
      }
      const updatedLabels = email.autoLabels.filter((label) => label.id !== labelId);
      try {
        await removeMessageAutoLabel(email.accountId, emailId, labelId);
        // Update email store (for main list view)
        useEmailStore.getState().updateEmailInPlace(emailId, {
          autoLabels: updatedLabels,
        });
        // Update sender digest (for sender group view / digest popup)
        useSenderGroupingStore.getState().updateDigestEmail(emailId, {
          autoLabels: updatedLabels,
        });
      } catch (error) {
        console.error('Failed to remove label from message', error);
      }
    },
    [emails]
  );

  const [showEmptyState, setShowEmptyState] = useState(false);
  // A failed load must say SO — "No emails found" for a non-empty folder is a
  // lie. Error keyed per mailbox; Retry below goes through onRefresh, which
  // calls loadEmails directly (bypassing the mailbox-key dedupe ref).
  const currentMailboxKey = selectedAccountId && selectedFolderId
    ? `${selectedAccountId}:${selectedFolderId}`
    : null;
  const emailLoadError = useEmailStore((state) =>
    currentMailboxKey ? state.emailLoadErrors[currentMailboxKey] ?? null : null
  );
  const [isPendingDialogOpen, setIsPendingDialogOpen] = useState(false);
  const emptyStateTimerRef = useRef<number | null>(null);
  const hasListContent = showGroupedSummary
    ? senderGroups.length > 0
    : briefingModeActive
      ? briefingRows.length > 0 || Boolean(briefingScope)
      : visibleEmails.length > 0;
  const hasContent = hasListContent || showPendingBanner;
  // Show loading state when initially loading OR when loading label filter with no content yet
  const isLabelFilterInitialLoading = Boolean(useServerLabelFilter) && isLoadingLabelFilter && listSourceKind !== 'labelFilter';
  const isInitialLoadingState =
    (isLoadingInitial && !hasContent) ||
    isLabelFilterInitialLoading ||
    // Briefing-only list: don't flash the "Briefing clear" empty state while
    // the briefing itself is still loading.
    (briefingModeActive && briefingLoading && briefingRows.length === 0 && !briefingScope);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setShowEmptyState(!hasContent && !isInitialLoadingState);
      return;
    }
    if (emptyStateTimerRef.current !== null) {
      window.clearTimeout(emptyStateTimerRef.current);
      emptyStateTimerRef.current = null;
    }
    if (hasContent || isInitialLoadingState) {
      setShowEmptyState(false);
      return;
    }
    setShowEmptyState(false);
    emptyStateTimerRef.current = window.setTimeout(() => {
      setShowEmptyState(true);
      emptyStateTimerRef.current = null;
    }, 200);
    return () => {
      if (emptyStateTimerRef.current !== null) {
        window.clearTimeout(emptyStateTimerRef.current);
        emptyStateTimerRef.current = null;
      }
    };
  }, [hasContent, isInitialLoadingState]);

  useEffect(() => {
    return () => {
      if (emptyStateTimerRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(emptyStateTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!pendingFeatureEnabled) {
      if (isPendingDialogOpen) {
        setIsPendingDialogOpen(false);
      }
      return;
    }
    if (!isPendingDialogOpen) {
      return;
    }
    if (pendingSenderEntries.length === 0) {
      setIsPendingDialogOpen(false);
    }
  }, [isPendingDialogOpen, pendingFeatureEnabled, pendingSenderEntries.length]);

  const dragHelpers = useEmailDrag({
    draggingEmailId,
    draggingSenderGroupKey,
    draggingSenderGroupCount,
  });

  // Ctrl+A: the registry emits this event; the mounted list answers with ITS
  // visible (filtered/grouped) ids — never the raw loaded store list (F7).
  // (The old useEmailListKeyboard Escape listener is gone: "exit sender group"
  // is a registry Escape command with a priority + input-context guards.)
  useEffect(() => {
    return mailEvents.on('mail:selectAllVisible', () => {
      if (!visibleEmailIds.length) {
        return;
      }
      onSelectAllVisible(visibleEmailIds);
      onBulkEditModeChange(true);
    });
  }, [visibleEmailIds, onSelectAllVisible, onBulkEditModeChange]);

  const resolveEntryAccountId = useCallback(
    (entry: PendingSenderEntry): string | null => {
      if (entry.accountId && entry.accountId !== UNIFIED_ACCOUNT_ID) {
        return entry.accountId;
      }
      if (selectedAccountId && selectedAccountId !== UNIFIED_ACCOUNT_ID) {
        return selectedAccountId;
      }
      // Don't return UNIFIED_ACCOUNT_ID - the contact store will resolve the actual account
      return null;
    },
    [selectedAccountId],
  );

  const runPendingEntryAction = useCallback(
    async (entry: PendingSenderEntry, action: 'approve' | 'block') => {
      if (!pendingFeatureEnabled) {
        return;
      }
      if (entry.contactId) {
        const accountContext = resolveEntryAccountId(entry);
        // Pass accountId if available, otherwise let contact store resolve from contact data
        const accountOption = accountContext ? { accountId: accountContext } : undefined;
        const operationId = entry.operationId;
        markOperationPending(operationId);
        try {
          if (action === 'approve') {
            await approveContactDirect(entry.contactId, accountOption);
          } else {
            await blockContactDirect(entry.contactId, accountOption);
          }
          markOperationComplete(operationId);
        } catch (error) {
          console.error(`Failed to ${action} pending contact`, error);
          markOperationError(operationId);
          // Rethrow (C10): the per-entry dismissal revert in the callers
          // depends on seeing the rejection — swallowing here dismissed
          // failed entries with no revert and no toast.
          throw error;
        }
        return;
      }
      if (entry.email) {
        if (action === 'approve') {
          await Promise.resolve(handleApproveSender(entry.email.id));
        } else {
          await Promise.resolve(handleBlockSender(entry.email.id));
        }
        return;
      }
      console.warn('No actionable data for pending sender', entry);
    },
    [
      approveContactDirect,
      blockContactDirect,
      handleApproveSender,
      handleBlockSender,
      markOperationComplete,
      markOperationError,
      markOperationPending,
      resolveEntryAccountId,
      pendingFeatureEnabled,
    ],
  );

  const resolveEntryAccountIdRef = useRef(resolveEntryAccountId);
  resolveEntryAccountIdRef.current = resolveEntryAccountId;
  const onEmailSelectRef = useRef(onEmailSelect);
  onEmailSelectRef.current = onEmailSelect;
  const selectedFolderIdRef = useRef(selectedFolderId);
  selectedFolderIdRef.current = selectedFolderId;

  const handlePendingCardSelect = useCallback(
    (key: string) => {
      if (!pendingFeatureEnabled) {
        return;
      }
      const entry = pendingSenderMapRef.current.get(key);
      if (!entry || !entry.emailId) {
        return;
      }
      const accountContext = resolveEntryAccountIdRef.current(entry);
      const folderContext = entry.folderId ?? selectedFolderIdRef.current ?? null;
      onEmailSelectRef.current(entry.emailId, folderContext, accountContext ?? undefined, entry.threadId ?? null);
    },
    [pendingFeatureEnabled],
  );

  // Use refs for values that change frequently to keep callbacks stable
  const pendingSenderMapRef = useRef(pendingSenderMap);
  pendingSenderMapRef.current = pendingSenderMap;
  const runPendingEntryActionRef = useRef(runPendingEntryAction);
  runPendingEntryActionRef.current = runPendingEntryAction;

  const handlePendingCardApprove = useCallback(
    (key: string) => {
      if (!pendingFeatureEnabled) {
        return;
      }
      const entry = pendingSenderMapRef.current.get(key);
      if (!entry) {
        return;
      }
      // Optimistically dismiss from UI immediately
      setDismissedPendingKeys((prev) => new Set(prev).add(key));
      // Process in background; revert the dismissal if it fails (C10).
      void runPendingEntryActionRef.current(entry, 'approve').catch(() => {
        setDismissedPendingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        toast.error('Failed to approve sender — restored');
      });
    },
    [pendingFeatureEnabled],
  );

  const handlePendingCardBlock = useCallback(
    (key: string) => {
      if (!pendingFeatureEnabled) {
        return;
      }
      const entry = pendingSenderMapRef.current.get(key);
      if (!entry) {
        return;
      }
      // Optimistically dismiss from UI immediately
      setDismissedPendingKeys((prev) => new Set(prev).add(key));
      // Process in background; revert the dismissal if it fails (C10).
      void runPendingEntryActionRef.current(entry, 'block').catch(() => {
        setDismissedPendingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        toast.error('Failed to block sender — restored');
      });
    },
    [pendingFeatureEnabled],
  );

  const handlePendingCardUnsubscribe = useCallback(
    async (key: string, unsubscribeUrl: string) => {
      if (!pendingFeatureEnabled) {
        return;
      }
      const entry = pendingSenderMapRef.current.get(key);
      if (!entry || !entry.emailId || !entry.accountId || !entry.folderId) {
        // Just open the unsubscribe URL if we can't archive
        window.open(unsubscribeUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      // Open unsubscribe URL
      window.open(unsubscribeUrl, '_blank', 'noopener,noreferrer');

      // Dismiss from pending list immediately (contact stays pending for future emails)
      setDismissedPendingKeys((prev) => new Set(prev).add(key));

      // Archive through the engine (N12): the old direct moveEmail bypassed
      // the optimistic grace, undo entry, operation indicator, and failure
      // restore every other archive path gets.
      const { executeEmailAction } = await import('@/stores/emailCommands');
      const result = await executeEmailAction(
        'archive',
        { kind: 'messages', ids: [entry.emailId] },
        { contextAccountId: entry.accountId, contextFolderId: entry.folderId }
      );
      if (result.failedIds.includes(entry.emailId)) {
        // Engine already restored + toasted; revert the pending dismissal too.
        setDismissedPendingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [pendingFeatureEnabled],
  );

  const runPendingBulkAction = useCallback(async (action: 'approve' | 'block') => {
    if (!pendingFeatureEnabled || !pendingSenderEntries.length) {
      return;
    }
    const snapshot = [...pendingSenderEntries];
    const wasInSenderGroup = Boolean(activeGroupKey);

    // Optimistically dismiss all entries from UI immediately
    setDismissedPendingKeys((prev) => {
      const next = new Set(prev);
      snapshot.forEach((entry) => next.add(entry.key));
      return next;
    });

    // Close the dialog immediately
    setIsPendingDialogOpen(false);

    if (action === 'block' && wasInSenderGroup && onExitSenderGroup) {
      onExitSenderGroup();
    }

    // Entries WITH a contact go through the per-account bulk endpoints;
    // contact-less entries (the bulk path used to silently drop them — C10)
    // route through the same path the single card uses.
    const withContact = snapshot.filter((entry) => entry.accountId && entry.contactId);
    const withoutContact = snapshot.filter((entry) => !(entry.accountId && entry.contactId));

    const entriesByAccount = new Map<string, PendingSenderEntry[]>();
    for (const entry of withContact) {
      const existing = entriesByAccount.get(entry.accountId!) ?? [];
      existing.push(entry);
      entriesByAccount.set(entry.accountId!, existing);
    }

    const failedKeys = new Set<string>();
    const accountEntries = Array.from(entriesByAccount.entries());
    const results = await Promise.allSettled(
      accountEntries.map(async ([entryAccountId, entries]) => {
        const contactIds = entries
          .map((e) => e.contactId)
          .filter((id): id is string => id !== null);
        if (contactIds.length > 0) {
          if (action === 'approve') {
            await bulkApproveContacts(entryAccountId, contactIds);
          } else {
            await bulkBlockContacts(entryAccountId, contactIds);
          }
        }
        return entryAccountId;
      })
    );
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`Bulk ${action} failed`, result.reason);
        accountEntries[index][1].forEach((entry) => failedKeys.add(entry.key));
      } else {
        void useFolderStore.getState().loadFolders(accountEntries[index][0]).catch((error) => {
          console.error(`Failed to refresh folders after bulk ${action}:`, error);
        });
      }
    });

    for (const entry of withoutContact) {
      try {
        await runPendingEntryAction(entry, action);
      } catch (error) {
        console.error(`Failed to ${action} pending sender`, error);
        failedKeys.add(entry.key);
      }
    }

    if (failedKeys.size > 0) {
      // Revert the optimistic dismissal for the failed entries and say so.
      setDismissedPendingKeys((prev) => {
        const next = new Set(prev);
        failedKeys.forEach((key) => next.delete(key));
        return next;
      });
      toast.error(
        `${failedKeys.size} sender${failedKeys.size === 1 ? '' : 's'} failed to ${action} — restored`
      );
    }
  }, [pendingFeatureEnabled, pendingSenderEntries, activeGroupKey, onExitSenderGroup, runPendingEntryAction]);

  const handlePendingApproveAll = useCallback(
    () => runPendingBulkAction('approve'),
    [runPendingBulkAction]
  );

  const handlePendingBlockAll = useCallback(
    () => runPendingBulkAction('block'),
    [runPendingBulkAction]
  );

  const renderEmailRow = useCallback(
    (email: Email, index: number, options?: { disableSelection?: boolean; disableDrag?: boolean }) => {
      const disableSelection = options?.disableSelection ?? false;
      const disableDrag = options?.disableDrag ?? false;
      const isSelected = selectedEmailId === email.id;
      const isChecked = bulkSelection.has(email.id);
      const accountColor = accountColors?.[email.accountId] || null;
      const operationState = operationStates[email.id];
      const showCheckbox = isBulkEditMode && !disableSelection;

      const rowKey = `${email.accountId ?? 'unknown'}:${email.folderId ?? 'unknown'}:${email.id}`;
      return (
        <EmailListItem
          key={rowKey}
          email={email}
          index={index}
          isSelected={isSelected}
          isChecked={isChecked}
          showCheckbox={showCheckbox}
          accountColor={accountColor}
          showAccountColors={showAccountColors}
          operationState={operationState}
          draggingEmailId={draggingEmailId}
          disableDrag={disableDrag}
          onEmailSelect={onEmailSelect}
          onToggleBulkSelect={onToggleBulkSelect}
          onEmailMarkRead={onEmailMarkRead}
          onEmailMarkUnread={onEmailMarkUnread}
          onEmailArchive={onEmailArchive}
          onEmailDelete={onEmailDelete}
          onEmailApproveSender={handleApproveSenderFromRow}
          onEmailBlockSender={handleBlockSenderFromRow}
          onEmailReply={onEmailReply}
          onEmailReplyAll={onEmailReplyAll}
          onEmailForward={onEmailForward}
          onCreateTask={handleCreateTaskAction}
          onAddSenderLabel={labelsVisible ? (onAddSenderLabel ?? handleOpenLabelDialog) : undefined}
          onTrainSpam={onTrainSpam}
          onTrainGood={onTrainGood}
          onRemoveLabel={handleRemoveLabel}
          onEmailDragStart={onEmailDragStart}
          onEmailDragEnd={onEmailDragEnd}
          showRecipientNames={showRecipientNames}
          contactNameLookup={contactNameLookup}
        />
      );
    },
    [
      accountColors,
      bulkSelection,
      draggingEmailId,
      isBulkEditMode,
      contactNameLookup,
      onEmailArchive,
      handleApproveSenderFromRow,
      handleBlockSenderFromRow,
      handleCreateTaskAction,
      handleOpenLabelDialog,
      handleRemoveLabel,
      onEmailDelete,
      onEmailDragEnd,
      onEmailDragStart,
      onEmailMarkRead,
      onEmailMarkUnread,
      onEmailSelect,
      showRecipientNames,
      onToggleBulkSelect,
      operationStates,
      selectedEmailId,
      showAccountColors,
      onAddSenderLabel,
      onCreateTask,
    ],
  );

  // ---------------------------------------------------------------------------
  // Briefing mode row machinery: store actions, the pinned+email union items
  // array, and the type-branching renderer for the briefing virtualizer.
  // ---------------------------------------------------------------------------
  const briefingMarkDone = useBriefingStore((state) => state.markDone);
  const briefingArchiveAndDone = useBriefingStore((state) => state.archiveAndDone);
  const briefingEnterKindScope = useBriefingStore((state) => state.enterKindScope);
  const briefingEnterItemScope = useBriefingStore((state) => state.enterItemScope);
  const briefingExitScope = useBriefingStore((state) => state.exitScope);
  const briefingToggleKindCollapsed = useBriefingStore((state) => state.toggleKindCollapsed);

  const handleBriefingDone = useCallback(
    (item: BriefingItem) => {
      void briefingMarkDone(item);
    },
    [briefingMarkDone]
  );

  const handleBriefingArchiveDone = useCallback(
    (item: BriefingItem, refs: Array<{ email: Email; inInbox: boolean }>) => {
      void briefingArchiveAndDone(item, refs);
    },
    [briefingArchiveAndDone]
  );

  const handleBriefingEnterItemScope = useCallback(
    (item: BriefingItem, refs: Array<{ email: Email; inInbox: boolean }>) => {
      void briefingEnterItemScope(item, refs);
    },
    [briefingEnterItemScope]
  );

  const handleBriefingExitScope = useCallback(() => {
    void briefingExitScope();
  }, [briefingExitScope]);

  // Header filter chips: a kind chip enters/re-targets the kind scope (URL +
  // Back keep working through the existing scope machinery); All exits it.
  const handleBriefingSelectKind = useCallback(
    (kind: string | null) => {
      if (kind) {
        briefingEnterKindScope(kind);
      } else {
        void briefingExitScope();
      }
    },
    [briefingEnterKindScope, briefingExitScope]
  );

  // "See also" chip click: jump to the related item's drill-down. The lookup
  // resolves against LIVE items only (dead keys never render a chip), so a
  // miss here just means the target lapsed between renders — ignore it.
  const handleBriefingOpenRelated = useCallback(
    (key: string) => {
      const target = briefingItemByKey.get(key);
      if (target) {
        void briefingEnterItemScope(target, briefingRefsForItem(target));
      }
    },
    [briefingItemByKey, briefingEnterItemScope, briefingRefsForItem]
  );

  // Briefing shows ONLY briefing items now — the remaining-inbox tail below
  // them retired (the mailbox tab is one click away). The inbox emails still
  // LOAD (refsForItem resolves item refs against the live list for fresh
  // read/flag state); they just don't render here.
  const briefingListItems = useMemo<BriefingListRow[]>(() => {
    if (!briefingModeActive) {
      return [];
    }
    return briefingRows;
  }, [briefingModeActive, briefingRows]);

  const getBriefingRowKey = useCallback((row: BriefingListRow) => {
    if (row.type === 'email') {
      return `${row.email.accountId ?? 'unknown'}:${row.email.folderId ?? 'unknown'}:${row.email.id}`;
    }
    if (row.type === 'briefingItem') {
      return `briefing-item:${row.item.accountId}:${row.item.key}`;
    }
    return `briefing-section:${row.kind}`;
  }, []);

  const renderBriefingListRow = useCallback(
    (row: BriefingListRow, index: number) => {
      if (row.type === 'email') {
        return renderEmailRow(row.email, index);
      }
      if (row.type === 'briefingSection') {
        return (
          <BriefingSectionRow
            key={`briefing-section-${row.kind}`}
            kind={row.kind}
            collapsed={row.collapsed}
            onToggle={briefingToggleKindCollapsed}
          />
        );
      }
      const identity = `${row.item.accountId}:${row.item.key}`;
      return (
        <BriefingItemRow
          key={`briefing-item-${identity}`}
          item={row.item}
          refs={row.refs}
          isPending={briefingPendingKeys.has(row.item.key)}
          onMarkDone={handleBriefingDone}
          onArchiveDone={handleBriefingArchiveDone}
          onOpenEmail={onEmailSelect}
          onEnterItemScope={handleBriefingEnterItemScope}
          swipeDisabled={isBulkEditMode}
          related={row.related}
          onOpenRelated={handleBriefingOpenRelated}
        />
      );
    },
    [
      renderEmailRow,
      briefingToggleKindCollapsed,
      briefingPendingKeys,
      handleBriefingDone,
      handleBriefingArchiveDone,
      onEmailSelect,
      handleBriefingEnterItemScope,
      handleBriefingOpenRelated,
      isBulkEditMode,
    ]
  );

  const disableInfiniteScroll = false;

  // Compute effective pagination props based on whether we're using server-side label filtering
  const effectiveOnLoadMore = useServerLabelFilter ? loadMoreLabelFilteredEmails : onLoadMore;
  const effectiveHasMore = useServerLabelFilter ? hasMoreLabelFiltered : hasMore;
  const effectiveIsLoadingMore = useServerLabelFilter ? isLoadingLabelFilter : isLoadingMore;

  // Re-check alias suggestions whenever the set of pending senders changes.
  const pendingAliasCheckKey = useMemo(
    () => pendingSenderCards.map((card) => card.key).join('|'),
    [pendingSenderCards]
  );

  // Pending-senders banner. (The inline survey banner that used to share this
  // slot retired with the Surveys sub-view tab — its count lives on the tab.)
  const surveyAndPendingNode = showPendingBanner ? (
    <>
    <PendingAliasSuggestions pendingKey={pendingAliasCheckKey} />
    <NewSendersBanner
      pendingSenders={pendingSenderCards}
      isLoading={pendingContactsLoading}
      dialogOpen={isPendingDialogOpen}
      onDialogOpenChange={setIsPendingDialogOpen}
      onSelect={handlePendingCardSelect}
      onApprove={handlePendingCardApprove}
      onBlock={handlePendingCardBlock}
      onUnsubscribe={handlePendingCardUnsubscribe}
      onApproveAll={handlePendingApproveAll}
      onBlockAll={handlePendingBlockAll}
      bulkActionState={pendingBulkAction}
      bulkActionProgress={pendingBulkProgress}
      onOpenContacts={onViewPendingContacts}
    />
    </>
  ) : null;

  const listDecorators = surveyAndPendingNode;
  const labelDialog = (
    <AddSenderToLabelDialog
      open={labelDialogOpen}
      onOpenChange={(open) => {
        setLabelDialogOpen(open);
        if (!open) {
          setLabelDialogEmailId(null);
          setLabelDialogError(null);
        }
      }}
      labels={senderLabels}
      selectedLabelId={selectedLabelId}
      onSelectLabel={setSelectedLabelId}
      onConfirm={handleConfirmLabelDialog}
      isSubmitting={labelDialogPending}
      error={labelDialogError}
    />
  );

  const taskPopoverOverlay = taskCreationEmail ? (
    <EmailTaskCreationProvider email={taskCreationEmail} summary={taskCreationSummary}>
      <TaskCreationPopoverController
        request={taskCreationRequest}
        onClose={() => setTaskCreationRequest(null)}
      >
        {null}
      </TaskCreationPopoverController>
    </EmailTaskCreationProvider>
  ) : null;

  const eventPopoverOverlay = eventCreationEmail ? (
    <EmailEventCreationProvider email={eventCreationEmail}>
      <EventCreationPopoverController
        email={eventCreationEmail}
        onClose={() => setEventCreationEmail(null)}
      />
    </EmailEventCreationProvider>
  ) : null;

  const overlays = (
    <>
      {taskPopoverOverlay}
      {eventPopoverOverlay}
      {labelDialog}
    </>
  );

  // Self-contained briefing page header: identity, freshness/stale/notes,
  // stats, and the kind filter chips. Hidden during an item drill-down (the
  // scope header owns that view).
  const briefingPageHeaderNode =
    briefingModeActive && briefingHeader && briefingScope?.type !== 'item' ? (
      <BriefingPageHeader
        header={briefingHeader}
        activeKind={briefingScope?.type === 'kind' ? briefingScope.kind : null}
        onSelectKind={handleBriefingSelectKind}
      />
    ) : null;
  // Label chips get their own row now that the tabs occupy the header slot;
  // the row (and the space it takes) disappears entirely while labels are off.
  // h-12 matches the standard tab-page header row (briefing/triage/surveys).
  const labelFilterRow = showLabelFilterUi ? (
    <div className="hidden h-12 shrink-0 items-center border-b border-border/60 px-4 md:flex">
      <LabelFilterBar
        labels={labelFilterOptions}
        activeLabelId={labelFilter}
        onSelect={onLabelFilterChange}
        noLabelOption={noLabelFilterOption}
      />
    </div>
  ) : null;

  // Handle loading and empty states
  if (isInitialLoadingState) {
    return (
      <>
        <div className="flex h-full w-full min-w-0 flex-col overflow-hidden glass-page animate-fade-in-up">
          <ListHeader
            labelFilterLabels={showLabelFilterUi ? labelFilterOptions : undefined}
            labelFilterActiveLabelId={showLabelFilterUi ? labelFilter : undefined}
            labelFilterNoLabelOption={showLabelFilterUi ? noLabelFilterOption : undefined}
            onLabelFilterChange={showLabelFilterUi ? onLabelFilterChange : undefined}
          />
          {briefingPageHeaderNode}
          {labelFilterRow}
          <div className="flex h-[60vh] items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        </div>
        {overlays}
      </>
    );
  }

  if (showEmptyState) {
    return (
      <>
        <div className="flex h-full w-full min-w-0 flex-col overflow-hidden glass-page animate-fade-in-up">
          <ListHeader
            labelFilterLabels={showLabelFilterUi ? labelFilterOptions : undefined}
            labelFilterActiveLabelId={showLabelFilterUi ? labelFilter : undefined}
            labelFilterNoLabelOption={showLabelFilterUi ? noLabelFilterOption : undefined}
            onLabelFilterChange={showLabelFilterUi ? onLabelFilterChange : undefined}
          />
          {briefingPageHeaderNode}
          {labelFilterRow}
          {emailLoadError ? (
            <div className="p-6">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Couldn't load this folder</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>{emailLoadError}</p>
                  <Button size="sm" variant="outline" onClick={() => onRefresh?.()}>
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          ) : briefingModeActive ? (
            <div className="flex flex-col items-center gap-2 p-10 text-center">
              <Sparkles className="h-6 w-6 text-muted-foreground/60" />
              <p className="text-sm font-medium">Briefing clear</p>
              <p className="text-sm text-muted-foreground">
                No active items. New ones appear when the next briefing runs.
              </p>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {showGroupedSummary ? 'No senders found' : 'No emails found'}
            </div>
          )}
        </div>
        {overlays}
      </>
    );
  }

  return (
    <>
      <div className="flex h-full w-full min-w-0 flex-col overflow-hidden glass-page animate-fade-in-up">
        <ListHeader
          labelFilterLabels={showLabelFilterUi ? labelFilterOptions : undefined}
          labelFilterActiveLabelId={showLabelFilterUi ? labelFilter : undefined}
          labelFilterNoLabelOption={showLabelFilterUi ? noLabelFilterOption : undefined}
          onLabelFilterChange={showLabelFilterUi ? onLabelFilterChange : undefined}
        />
        {briefingPageHeaderNode}
        {labelFilterRow}

        {/* A8 gap (N18): with a cached snapshot on screen, a load failure
            previously showed STALE rows with no hint — the error Alert only
            rendered in the empty-state branch. */}
        {emailLoadError && (
          <div className="px-3 pt-2">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Couldn't refresh this folder — showing cached mail</AlertTitle>
              <AlertDescription className="flex items-center gap-3">
                <span className="min-w-0 truncate">{emailLoadError}</span>
                <Button size="sm" variant="outline" onClick={() => onRefresh?.()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {groupBySender && activeSenderData && (
          <SenderGroupHeader
            activeGroup={activeSenderGroup ?? null}
            activeSenderData={activeSenderData as SenderGroupEntry}
            activeSenderInfo={activeSenderInfo}
            senderGroupAction={activeSenderGroupAction ?? null}
            isSenderDigestLoading={isSenderDigestLoading}
            isSenderDigestVisible={isSenderDigestVisible}
            onSenderArchiveAll={onSenderArchiveAll}
            onSenderDeleteAll={onSenderDeleteAll}
            onSenderGroupAction={onSenderGroupAction}
            onShowDigest={onShowDigest}
            onExitSenderGroup={requestExitSenderGroup}
          />
        )}

        {briefingModeActive && briefingScope?.type === 'item' && (
          <BriefingScopeHeader
            item={briefingScopeItem}
            refs={briefingScopeItemRefs}
            isPending={briefingScopeItem ? briefingPendingKeys.has(briefingScopeItem.key) : false}
            onExit={handleBriefingExitScope}
            onMarkDone={handleBriefingDone}
            onArchiveDone={handleBriefingArchiveDone}
            related={briefingScopeItem ? briefingRelatedForItem(briefingScopeItem) : undefined}
            onOpenRelated={handleBriefingOpenRelated}
          />
        )}

        {showGroupedSummary ? (
          // Grouped view - render groups directly (groups are usually small, <50)
          <EmailListVirtualized
            items={senderGroups}
            renderItem={(group, index) => {
              const messageCount = group.emails.length;
              const knownTotal =
                typeof group.totalMessages === 'number' && Number.isFinite(group.totalMessages)
                  ? Math.max(group.totalMessages, messageCount)
                  : messageCount;

              // Only render as single email if we know there's truly just one email total
              if (messageCount === 1 && knownTotal <= 1) {
                return renderEmailRow(group.emails[0], index);
              }

              // Note: groups with 0 emails are filtered out before reaching here,
              // so we don't need the messageCount === 0 check anymore

              return (
                <SenderGroupItem
                  key={`sender-group-${group.key}`}
                  group={group}
                  index={index}
                  showAccountColors={showAccountColors}
                  showRecipientNames={showRecipientNames}
                  contactNameLookup={contactNameLookup}
                  accountColors={accountColors}
                  hasMore={hasMore}
                  onSenderGroupSelect={onSenderGroupSelect}
                  onSenderGroupOpenLatestEmail={onSenderGroupOpenLatestEmail}
                  onSenderGroupDragStart={onSenderGroupDragStart}
                  onSenderGroupDragEnd={onSenderGroupDragEnd}
                  onSenderGroupAction={onSenderGroupAction}
                  senderGroupAction={pendingGroupActions[group.key] ?? null}
                  draggingState={{ getGroupDragState: dragHelpers.getSenderGroupDragState }}
                  bulkSelection={bulkSelection}
                  onToggleBulkSelect={onToggleBulkSelect}
                  isBulkEditMode={isBulkEditMode}
                  activeLabelId={labelFilter === 'all' ? null : labelFilter}
                />
              );
            }}
            estimateSize={100}
            onLoadMore={effectiveOnLoadMore}
            hasMore={effectiveHasMore}
            isLoadingMore={effectiveIsLoadingMore}
            disableInfiniteScroll={disableInfiniteScroll}
            getItemKey={(group) => group.key}
            accountId={selectedAccountId}
            folderId={selectedFolderId}
            senderGroupEmail={activeSenderGroup?.email ?? null}
            isListVisible={isListVisible}
            isLoadingInitial={isLoadingInitial}
          >
            {listDecorators}
          </EmailListVirtualized>
        ) : briefingModeActive && briefingScope?.type !== 'item' ? (
          // Briefing mode: pinned briefing rows (items + collapsed kinds) above
          // the suppression-filtered flat list, one virtualizer, union rows.
          // (An item drill-down replaces the email list itself and renders
          // through the regular flat branch below.)
          <EmailListVirtualized
            items={briefingListItems}
            renderItem={renderBriefingListRow}
            estimateSize={110}
            onLoadMore={undefined}
            hasMore={false}
            isLoadingMore={false}
            disableInfiniteScroll={disableInfiniteScroll}
            getItemKey={getBriefingRowKey}
            accountId={selectedAccountId}
            folderId={selectedFolderId}
            // Own scroll-restore bucket per briefing view: the row universe
            // differs from flat mode (and per kind scope), so sharing the
            // mailbox bucket clobbered its saved position with keys the other
            // view can't resolve — landing somewhere unrelated on the way back.
            senderGroupEmail={
              briefingScope?.type === 'kind' ? `briefing-kind:${briefingScope.kind}` : 'briefing'
            }
            isListVisible={isListVisible}
            isLoadingInitial={isLoadingInitial}
          >
            {listDecorators}
          </EmailListVirtualized>
        ) : (
          // Regular email list - use virtualization
          <EmailListVirtualized
            items={scopeConstrainedEmails}
            renderItem={renderEmailRow}
            estimateSize={100}
            onLoadMore={effectiveOnLoadMore}
            hasMore={effectiveHasMore}
            isLoadingMore={effectiveIsLoadingMore}
            disableInfiniteScroll={disableInfiniteScroll}
            getItemKey={(email) => `${email.accountId ?? 'unknown'}:${email.folderId ?? 'unknown'}:${email.id}`}
            accountId={selectedAccountId}
            folderId={selectedFolderId}
            // A briefing item drill-down renders through this branch — give it
            // its own scroll-restore bucket so it never clobbers the mailbox's.
            senderGroupEmail={
              briefingModeActive && briefingScope?.type === 'item'
                ? `briefing-item:${briefingScope.accountId}:${briefingScope.key}`
                : activeSenderGroup?.email ?? null
            }
            isListVisible={isListVisible}
            isLoadingInitial={isLoadingInitial}
            // Clear the floating mobile action bar shown for the active group
            footer={groupBySender && activeSenderData ? <div aria-hidden className="h-20 md:hidden" /> : undefined}
          >
            {listDecorators}
          </EmailListVirtualized>
        )}
        <BulkActionBar
          canApproveSenders={canApproveSenders}
          canBlockSenders={canBlockSenders}
          allVisibleSelected={allVisibleSelected}
          onSelectAllVisible={() => onSelectAllVisible(visibleEmailIds)}
        />
      </div>
      {overlays}
    </>
  );
}

export type { SenderGroupInfo, ActiveSenderGroup, SenderGroupQuickAction } from './types';

function TaskCreationPopoverController({
  request,
  onClose,
  children,
}: {
  request: TaskCreationRequest | null;
  onClose: () => void;
  children: ReactNode;
}) {
  const { openPopover, popoverState } = useEmailTaskCreation();
  const hasOpenedRef = useRef(false);
  const lastRequestKeyRef = useRef<number | null>(null);
  const anchorPortalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (anchorPortalRef.current) {
        anchorPortalRef.current.remove();
        anchorPortalRef.current = null;
      }
    };
  }, []);

  const ensureAnchorElement = useCallback((rect: DOMRect): HTMLDivElement | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    let el = anchorPortalRef.current;
    if (!el) {
      el = document.createElement('div');
      anchorPortalRef.current = el;
      document.body.appendChild(el);
    }
    const top = rect.top + window.scrollY;
    const left = rect.left + window.scrollX;
    Object.assign(el.style, {
      position: 'absolute',
      width: `${rect.width || 1}px`,
      height: `${rect.height || 1}px`,
      top: `${top}px`,
      left: `${left}px`,
      pointerEvents: 'none',
      transform: 'translate3d(0,0,0)',
    });
    return el;
  }, []);

  useEffect(() => {
    if (!request?.anchor) {
      return;
    }
    if (lastRequestKeyRef.current === request.key && hasOpenedRef.current) {
      return;
    }
    const anchorEl = ensureAnchorElement(request.anchor.rect);
    if (!anchorEl) {
      return;
    }

    hasOpenedRef.current = false;
    lastRequestKeyRef.current = request.key;
    openPopover(anchorEl, {
      align: request.align ?? 'end',
      side: request.side ?? 'top',
      width: request.width,
    });
  }, [request, openPopover, popoverState.open, ensureAnchorElement]);

  useEffect(() => {
    if (!request) {
      return;
    }
    if (popoverState.open) {
      hasOpenedRef.current = true;
      return;
    }
    if (hasOpenedRef.current) {
      onClose();
      if (anchorPortalRef.current) {
        anchorPortalRef.current.remove();
        anchorPortalRef.current = null;
      }
    }
  }, [request, popoverState.open, onClose]);

  return <>{children}</>;
}

function EventCreationPopoverController({
  email,
  onClose,
}: {
  email: Email;
  onClose: () => void;
}) {
  const { openPopover, popoverState } = useEmailEventCreation();
  const hasOpenedRef = useRef(false);
  const emailIdRef = useRef<string | null>(null);
  const anchorPortalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (anchorPortalRef.current) {
        anchorPortalRef.current.remove();
        anchorPortalRef.current = null;
      }
    };
  }, []);

  const ensureAnchorElement = useCallback((): HTMLDivElement | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    let el = anchorPortalRef.current;
    if (!el) {
      el = document.createElement('div');
      anchorPortalRef.current = el;
      document.body.appendChild(el);
    }
    // Center the anchor in the viewport
    const top = window.innerHeight / 3;
    const left = window.innerWidth / 2 - 360;
    Object.assign(el.style, {
      position: 'fixed',
      width: '720px',
      height: '1px',
      top: `${top}px`,
      left: `${left}px`,
      pointerEvents: 'none',
      transform: 'translate3d(0,0,0)',
    });
    return el;
  }, []);

  useEffect(() => {
    if (emailIdRef.current === email.id && hasOpenedRef.current) {
      return;
    }
    const anchorEl = ensureAnchorElement();
    if (!anchorEl) {
      return;
    }

    hasOpenedRef.current = false;
    emailIdRef.current = email.id;
    openPopover(anchorEl, {
      align: 'center',
      side: 'bottom',
      width: 720,
    });
  }, [email.id, openPopover, ensureAnchorElement]);

  useEffect(() => {
    if (popoverState.open) {
      hasOpenedRef.current = true;
      return;
    }
    if (hasOpenedRef.current) {
      onClose();
      if (anchorPortalRef.current) {
        anchorPortalRef.current.remove();
        anchorPortalRef.current = null;
      }
    }
  }, [popoverState.open, onClose]);

  return null;
}

